<template>
  <div
    class="sidebar__menu-block"
    ph-autocapture="sidebar"
    data-highlight="menu"
  >
    <ul class="tree">
      <SidebarSearch
        :selected-workspace="selectedWorkspace"
        @open-workspace-search="openWorkspaceSearch"
      />
      <nuxt-link
        v-if="$canNavCapability('nav.home', selectedWorkspace.id)"
        v-slot="{ href, navigate, isExactActive }"
        custom
        :to="{
          name: 'workspace',
          params: {
            workspaceId: selectedWorkspace.id,
          },
        }"
      >
        <li
          class="tree__item"
          :class="{
            active: isExactActive,
          }"
        >
          <div class="tree__action sidebar__action">
            <a :href="href" class="tree__link" @click="navigate">
              <i class="tree__icon iconoir-home-simple"></i>
              <span class="tree__link-text">
                <span class="sidebar__item-name">{{ $t('sidebar.home') }}</span>
              </span>
            </a>
          </div>
        </li>
      </nuxt-link>

      <li
        v-if="$canNavCapability('nav.notifications', selectedWorkspace.id)"
        class="tree__item"
      >
        <div class="tree__action tree__action--has-counter">
          <a
            class="tree__link"
            @click="$refs.notificationPanel.toggle($event.currentTarget)"
          >
            <i class="tree__icon tree__icon--type iconoir-bell"></i>
            <span class="tree__link-text">{{
              $t('sidebar.notifications')
            }}</span>
          </a>
          <BadgeCounter
            v-show="unreadNotificationCount"
            class="tree__counter"
            :count="unreadNotificationCount"
            :limit="10"
          >
          </BadgeCounter>
        </div>
        <NotificationPanel ref="notificationPanel" />
      </li>

      <nuxt-link
        v-if="
          $canNavCapability('nav.members', selectedWorkspace.id) &&
          $hasPermission(
            'workspace.list_workspace_users',
            selectedWorkspace,
            selectedWorkspace.id
          )
        "
        v-slot="{ href, navigate, isExactActive }"
        custom
        :to="{
          name: 'settings-members',
          params: {
            workspaceId: selectedWorkspace.id,
          },
        }"
      >
        <li
          class="tree__item"
          :class="{
            active: isExactActive,
          }"
          data-highlight="members"
        >
          <div class="tree__action sidebar__action">
            <a :href="href" class="tree__link" @click="navigate">
              <i class="tree__icon iconoir-group"></i>
              <span class="tree__link-text">
                <span class="sidebar__item-name">{{
                  $t('sidebar.members')
                }}</span>
              </span>
            </a>
          </div>
        </li>
      </nuxt-link>

      <SidebarNavGroup
        v-if="showAiManagementGroup"
        :title="$t('sidebar.aiManagement')"
        icon="iconoir-brain"
      >
        <ul class="tree__subs">
          <nuxt-link
            v-if="canSeeAgents"
            v-slot="{ href, navigate, isExactActive }"
            :to="{
              name: 'settings-agents',
              params: {
                workspaceId: selectedWorkspace.id,
              },
            }"
          >
            <li
              class="tree__sub"
              :class="{ active: isExactActive }"
              data-highlight="agents"
            >
              <a :href="href" class="tree__sub-link" @click="navigate">
                <span class="tree__sub-name">{{ $t('sidebar.agents') }}</span>
              </a>
            </li>
          </nuxt-link>
          <component
            :is="component"
            v-for="(component, index) in aiSidebarWorkspaceComponents"
            :key="'aiSidebarWorkspaceComponents' + index"
            compact
            :workspace="selectedWorkspace"
            :right-sidebar-open="rightSidebarOpen"
          ></component>
        </ul>
      </SidebarNavGroup>

      <component
        :is="component"
        v-for="(component, index) in nonAiSidebarWorkspaceComponents"
        :key="'sidebarWorkspaceComponents' + index"
        :workspace="selectedWorkspace"
        :right-sidebar-open="rightSidebarOpen"
      ></component>
    </ul>
    <SidebarTablesMenu :workspace="selectedWorkspace" />
    <ul class="tree">
      <SidebarNavGroup
        v-if="$canNavCapability('nav.campaigns', selectedWorkspace.id)"
        :title="$t('sidebar.campaigns')"
        icon="iconoir-megaphone"
        :default-collapsed="!campaignsNavActive"
      >
        <ul class="tree__subs">
          <nuxt-link
            v-slot="{ href, navigate, isExactActive }"
            :to="{
              name: 'workspace-campaign-dashboard',
              params: {
                workspaceId: selectedWorkspace.id,
              },
            }"
          >
            <li
              class="tree__sub"
              :class="{ active: isExactActive }"
              data-highlight="campaign-dashboard"
            >
              <a :href="href" class="tree__sub-link" @click="navigate">
                <span class="tree__sub-name">{{
                  $t('sidebar.campaignDashboard')
                }}</span>
              </a>
            </li>
          </nuxt-link>
          <nuxt-link
            v-slot="{ href, navigate, isExactActive }"
            :to="{
              name: 'workspace-campaigns',
              params: {
                workspaceId: selectedWorkspace.id,
              },
            }"
          >
            <li
              class="tree__sub"
              :class="{ active: isExactActive }"
              data-highlight="campaigns"
            >
              <a :href="href" class="tree__sub-link" @click="navigate">
                <span class="tree__sub-name">{{
                  $t('sidebar.campaignList')
                }}</span>
              </a>
            </li>
          </nuxt-link>
          <nuxt-link
            v-if="campaignApprovalsAvailable"
            v-slot="{ href, navigate, isExactActive }"
            :to="{
              name: 'workspace-campaign-approvals',
              params: {
                workspaceId: selectedWorkspace.id,
              },
            }"
          >
            <li
              class="tree__sub"
              :class="{ active: isExactActive }"
              data-highlight="campaign-approvals"
            >
              <a :href="href" class="tree__sub-link" @click="navigate">
                <span class="tree__sub-name">{{
                  $t('sidebar.campaignApprovals')
                }}</span>
              </a>
            </li>
          </nuxt-link>
        </ul>
      </SidebarNavGroup>
      <nuxt-link
        v-if="
          $canNavCapability('nav.solution_kits', selectedWorkspace.id) &&
          $hasPermission(
            'workspace.list_workspace_users',
            selectedWorkspace,
            selectedWorkspace.id
          )
        "
        v-slot="{ href, navigate, isExactActive }"
        :to="{
          name: 'settings-kits',
          params: {
            workspaceId: selectedWorkspace.id,
          },
        }"
      >
        <li
          class="tree__item"
          :class="{
            active: isExactActive,
          }"
          data-highlight="solution-kits"
        >
          <div class="tree__action sidebar__action">
            <a :href="href" class="tree__link" @click="navigate">
              <i class="tree__icon iconoir-shop"></i>
              <span class="tree__link-text">
                <span class="sidebar__item-name">
                  {{ $t('sidebar.solutionKits') }}
                </span>
              </span>
            </a>
          </div>
        </li>
      </nuxt-link>
      <li
        v-if="$canNavCapability('nav.trash', selectedWorkspace.id)"
        class="tree__item"
      >
        <div class="tree__action sidebar__action">
          <a class="tree__link" @click="$refs.trashModal.show()">
            <i class="tree__icon iconoir-bin"></i>
            <span class="tree__link-text">
              <span class="sidebar__item-name">{{ $t('sidebar.trash') }}</span>
            </span>
          </a>
          <TrashModal
            ref="trashModal"
            :initial-workspace="selectedWorkspace"
          ></TrashModal>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import TrashModal from '@baserow/modules/core/components/trash/TrashModal'
import NotificationPanel from '@baserow/modules/core/components/NotificationPanel'
import BadgeCounter from '@baserow/modules/core/components/BadgeCounter'
import SidebarSearch from '@baserow/modules/core/components/sidebar/SidebarSearch'
import SidebarTablesMenu from '@baserow/modules/core/components/sidebar/SidebarTablesMenu'
import SidebarNavGroup from '@baserow/modules/core/components/sidebar/SidebarNavGroup'
import { isCampaignApprovalsAvailable } from '@baserow/modules/core/legacyVokalisFeatureAvailability'

export default {
  name: 'SidebarMenu',
  components: {
    TrashModal,
    NotificationPanel,
    BadgeCounter,
    SidebarSearch,
    SidebarTablesMenu,
    SidebarNavGroup,
  },
  props: {
    selectedWorkspace: {
      type: Object,
      required: true,
    },
    rightSidebarOpen: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['open-workspace-search'],
  computed: {
    sidebarWorkspaceComponents() {
      return Object.values(this.$registry.getAll('plugin'))
        .flatMap((plugin) =>
          plugin.getSidebarWorkspaceComponents(this.selectedWorkspace)
        )
        .filter((component) => component !== null)
    },
    aiSidebarWorkspaceComponents() {
      return this.sidebarWorkspaceComponents.filter((component) =>
        this.isAiSidebarComponent(component)
      )
    },
    nonAiSidebarWorkspaceComponents() {
      return this.sidebarWorkspaceComponents.filter(
        (component) => !this.isAiSidebarComponent(component)
      )
    },
    canSeeAgents() {
      return (
        this.$canNavCapability('nav.agents', this.selectedWorkspace.id) &&
        this.$hasPermission(
          'workspace.list_workspace_users',
          this.selectedWorkspace,
          this.selectedWorkspace.id
        )
      )
    },
    campaignApprovalsAvailable() {
      return isCampaignApprovalsAvailable(this)
    },
    showAiManagementGroup() {
      return this.canSeeAgents || this.aiSidebarWorkspaceComponents.length > 0
    },
    campaignsNavActive() {
      return [
        'workspace-campaign-dashboard',
        'workspace-campaigns',
        'workspace-campaign-approvals',
      ].includes(this.$route.name)
    },
    ...mapGetters({
      unreadNotificationCount: 'notification/getUnreadCount',
    }),
  },
  methods: {
    isAiSidebarComponent(component) {
      return (
        component?.name === 'CopilotSidebarMenuEntry' ||
        component?.options?.name === 'CopilotSidebarMenuEntry'
      )
    },
    openWorkspaceSearch() {
      this.$emit('open-workspace-search')
    },
  },
}
</script>
